import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SEO from "@/components/SEO";
import { FileUploadSection } from "@/components/opportunities/rate-card/FileUploadSection";
import { RateCardOpportunityCard } from "@/components/opportunities/rate-card/RateCardOpportunityCard";
import { getCatalog } from "@/lib/queries/catalog";
import { Link } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { useRealtimeRun, TriggerAuthContext } from "@trigger.dev/react-hooks";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";

// Types
export interface OpportunityData {
  opportunity_id: string;
  opportunity_name: string;
  catalog_data: {
    min_volunteers: number;
    max_volunteers: number;
    description: string;
    format: string;
    title: string;
    prerequisites: string[];
    timeline: any[];
    faqs: string;
    community_need: string;
    volunteer_experience: string;
    volunteer_responsibilities: string;
    volunteer_support: string;
    impact: string;
    output: string;
  };
  pricing_details: {
    currency: string;
    cost_per_unit: string;
    pricing_model: string;
    additional_fees: {
      program_management: {
        amount: string;
        fee_type: string;
      };
      material_kit: {
        amount: string;
        fee_type: string;
      };
      refreshments: {
        amount: string;
        fee_type: string;
      };
      goodies: {
        amount: string;
        fee_type: string;
      };
    };
    include_fees_in_base: boolean;
    additional_details: string;
    enableGeographicPricing: boolean;
    geographicMarkups: {
      locationId: string;
      locationName: string;
      markupPercentage: string;
    }[];
  };
  supported_locations: string[];
  image_url: string;
  similarity_score?: number;
}

export interface ParseResult {
  success: boolean;
  message: string;
  data: OpportunityData[];
}

const OpportunitiesRateCard = ({
  session,
  profile,
}: {
  session: any;
  profile: any;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [parseResult, setParseResult] = useState<any | null>(null);
  const [catalogResponse, setCatalogResponse] = useState<any[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [isCatalogLoading, setIsCatalogLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);

  // Debug: log when useEffect fires and why
  useEffect(() => {
    let didCancel = false;
    if (!session?.access_token) return;
    setIsCatalogLoading(true);
    const fetchCatalog = async () => {
      try {
        console.log("[OpportunitiesRateCard] Fetching catalog", {
          page: currentPage,
          limit: itemsPerPage,
        });
        const response = await getCatalog(session.access_token, {
          page: currentPage,
          limit: itemsPerPage,
        });
        if (!didCancel) {
          setCatalogResponse(response.data);
          setTotalPages(response.pagination?.totalPages || 1);
        }
      } catch (error: any) {
        if (!didCancel) setCatalogError(error.message);
      } finally {
        if (!didCancel) setIsCatalogLoading(false);
      }
    };
    fetchCatalog();
    return () => {
      didCancel = true;
    };
  }, [session?.access_token, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <SEO
        title="Opportunities Rate Card"
        description="Upload and parse opportunity rate cards from documents"
      />
      <div className="space-y-6">
        <h1 className="text-3xl font-bold mb-6">Opportunities Rate Card</h1>

        <div className="grid grid-cols-1 gap-6">
          {/* Upload Section */}
          <FileUploadSection
            isUploading={isUploading}
            uploadError={uploadError}
            setIsUploading={setIsUploading}
            setUploadError={setUploadError}
            setParseResult={setParseResult}
            session={session}
          />

          {/* Results Section */}
          {parseResult?.data?.id && parseResult.data.publicAccessToken && (
            <TriggerAuthContext.Provider
              value={{ accessToken: parseResult.data.publicAccessToken }}
            >
              <ParsedOpportunities
                runId={parseResult.data.id}
                publicAccessToken={parseResult.data.publicAccessToken}
                profile={profile}
                session={session}
              />
            </TriggerAuthContext.Provider>
          )}

          {/* Catalog Table Section */}
          <div className="space-y-2">
            <h2 className="text-xl font-semibold mt-8 mb-4">
              My Opportunities
            </h2>
            {isCatalogLoading ? (
              <p>Loading catalog...</p>
            ) : catalogError ? (
              <p className="text-red-500">Error: {catalogError}</p>
            ) : (
              catalogResponse && (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Image</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Format</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Volunteers</TableHead>
                        <TableHead>Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalogResponse.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>
                            {entry.images && entry.images.length > 0 ? (
                              <Link to={`/dashboard/catalog/${entry.id}`}>
                                <img
                                  src={entry.images[0].image_url}
                                  alt={entry.title}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              </Link>
                            ) : (
                              <Link to={`/dashboard/catalog/${entry.id}`}>
                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                  <span className="text-xs text-gray-500">
                                    No image
                                  </span>
                                </div>
                              </Link>
                            )}
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/dashboard/catalog/${entry.id}`}
                              className="text-primary underline"
                            >
                              {entry.title}
                            </Link>
                          </TableCell>
                          <TableCell>{entry.npo.org_name}</TableCell>
                          <TableCell>{entry.format}</TableCell>
                          <TableCell>{entry.duration}</TableCell>
                          <TableCell>
                            {entry.min_volunteers} - {entry.max_volunteers}
                          </TableCell>
                          <TableCell>{entry.event_cost}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {totalPages > 1 && (
                    <div className="flex justify-center mt-4 space-x-2">
                      <button
                        disabled={currentPage === 1}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i + 1}
                          className={`px-3 py-1 border rounded ${
                            currentPage === i + 1 ? "bg-primary text-white" : ""
                          }`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

function ParsedOpportunities({
  runId: propRunId,
  publicAccessToken: propAccessToken,
  profile,
  session,
}: {
  runId?: string;
  publicAccessToken?: string;
  profile: any;
  session: any;
}) {
  // Local storage keys
  const LS_RUN_ID = "opps_ratecard_runId";
  const LS_ACCESS_TOKEN = "opps_ratecard_accessToken";

  const [localRunId, setLocalRunId] = useState<string | undefined>(propRunId);
  const [localAccessToken, setLocalAccessToken] = useState<string | undefined>(
    propAccessToken
  );
  const [notification, setNotification] = useState<string | null>(null);

  // On mount, if props are not provided, check localStorage
  useEffect(() => {
    if (!propRunId || !propAccessToken) {
      const storedRunId = localStorage.getItem(LS_RUN_ID);
      const storedAccessToken = localStorage.getItem(LS_ACCESS_TOKEN);
      if (storedRunId && storedAccessToken) {
        setLocalRunId(storedRunId);
        setLocalAccessToken(storedAccessToken);
      }
    } else {
      // Store in localStorage
      localStorage.setItem(LS_RUN_ID, propRunId);
      localStorage.setItem(LS_ACCESS_TOKEN, propAccessToken);
      setLocalRunId(propRunId);
      setLocalAccessToken(propAccessToken);
    }
  }, [propRunId, propAccessToken]);

  // Remove from storage helper
  const clearStorage = () => {
    localStorage.removeItem(LS_RUN_ID);
    localStorage.removeItem(LS_ACCESS_TOKEN);
  };

  // Only call useRealtimeRun if we have IDs
  const { run, error: hookError } = useRealtimeRun(localRunId, {
    accessToken: localAccessToken,
  });

  // Watch run status for completion/failure
  useEffect(() => {
    if (run && (run.status === "COMPLETED" || run.status === "FAILED")) {
      setNotification(
        run.status === "COMPLETED"
          ? "Parsing completed successfully!"
          : "Parsing failed: " + (run.error?.message ?? "Unknown error")
      );
      clearStorage();
    }
  }, [run]);

  // Show notification and auto-dismiss
  useEffect(() => {
    if (notification) {
      const timeout = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timeout);
    }
  }, [notification]);

  // Notification UI
  if (notification) {
    return (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded shadow z-50">
        {notification}
      </div>
    );
  }

  // Hook-level error
  if (hookError) {
    return <p className="text-red-500">Error: {hookError.message}</p>;
  }
  // Loading run resource
  if (!run) {
    return <p>Loading run...</p>;
  }
  // Run-level failure
  if (run.status === "FAILED") {
    return (
      <p className="text-red-500">
        Parsing failed: {run.error?.message ?? "Unknown error"}
      </p>
    );
  }
  // Still running
  if (run.status !== "COMPLETED") {
    const progress = (run.metadata as any)?.progress;
    const status = (run.metadata as any)?.status;
    return (
      <div className="space-y-4 w-full">
        <Card className="w-full">
          <CardContent className="space-y-4 py-6">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                Processing document... {status}
              </span>
              {progress != null && typeof progress === "number" && (
                <span className="text-sm text-gray-500">{progress}%</span>
              )}
            </div>
            <Progress
              value={typeof progress === "number" ? progress : 0}
              className="w-full"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completed
  const parsed = run.output as ParseResult;
  if (!parsed?.data || !Array.isArray(parsed.data)) {
    return <p>No parse results available.</p>;
  }
  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">
          Identified Opportunities ({parsed.data.length})
        </h2>
      </div>
      {parsed.data.length > 0 ? (
        <Tabs defaultValue="0" className="w-full">
          <TabsList className="mb-4 w-full overflow-x-auto">
            {parsed.data.map((_, i) => (
              <TabsTrigger key={i} value={i.toString()}>
                Opportunity {i + 1}
              </TabsTrigger>
            ))}
          </TabsList>
          {parsed.data.map((opp, i) => (
            <TabsContent key={i} value={i.toString()}>
              <RateCardOpportunityCard
                opportunity={opp}
                profile={profile}
                session={session}
                getDefaultFormValues={() => opp}
              />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <div className="p-8 text-center border rounded-lg bg-gray-50">
          <p className="text-gray-500">
            No opportunities found in the document.
          </p>
        </div>
      )}
    </div>
  );
}

export default OpportunitiesRateCard;
